import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { AccordionModule } from 'primeng/accordion';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { environment } from '../../../../environments/environment';

interface FaqQuestion {
  id: string;
  questionKey: string;
  answerKey: string;
}

interface FaqCategory {
  id: string;
  icon: string;
  lucideIcon?: string;
  titleKey: string;
  questions: FaqQuestion[];
}

@Component({
  selector: 'app-help-support',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TranslocoModule,
    AccordionModule,
    LucideAngularModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ReactiveFormsModule,
  ],
  templateUrl: './help-support.component.html'
})
export class HelpSupportComponent implements OnInit {
  private sanitizer = inject(DomSanitizer);

  videoUrl: SafeResourceUrl = '';
  supportEmail = 'info@greentripper.org';
  selectedCategoryId = signal<string>('account');
  expandedPanels: string[] = [];

  // Contact form
  contactForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    subject: new FormControl('', [Validators.required]),
    message: new FormControl('', [Validators.required, Validators.minLength(10)])
  });

  isSubmitting = signal(false);
  submitSuccess = signal(false);
  submitError = signal(false);

  faqCategories: FaqCategory[] = [
    {
      id: 'account',
      icon: '',
      lucideIcon: 'users',
      titleKey: 'help.faq.categories.account.title',
      questions: [
        { id: 'createAccount', questionKey: 'help.faq.categories.account.questions.createAccount.question', answerKey: 'help.faq.categories.account.questions.createAccount.answer' },
        { id: 'editProfile', questionKey: 'help.faq.categories.account.questions.editProfile.question', answerKey: 'help.faq.categories.account.questions.editProfile.answer' },
        { id: 'inviteMembers', questionKey: 'help.faq.categories.account.questions.inviteMembers.question', answerKey: 'help.faq.categories.account.questions.inviteMembers.answer' },
        { id: 'roles', questionKey: 'help.faq.categories.account.questions.roles.question', answerKey: 'help.faq.categories.account.questions.roles.answer' },
      ]
    },
    {
      id: 'trips',
      icon: '',
      lucideIcon: 'map',
      titleKey: 'help.faq.categories.trips.title',
      questions: [
        { id: 'createTrip', questionKey: 'help.faq.categories.trips.questions.createTrip.question', answerKey: 'help.faq.categories.trips.questions.createTrip.answer' },
        { id: 'addElements', questionKey: 'help.faq.categories.trips.questions.addElements.question', answerKey: 'help.faq.categories.trips.questions.addElements.answer' },
        { id: 'calculate', questionKey: 'help.faq.categories.trips.questions.calculate.question', answerKey: 'help.faq.categories.trips.questions.calculate.answer' },
        { id: 'duplicate', questionKey: 'help.faq.categories.trips.questions.duplicate.question', answerKey: 'help.faq.categories.trips.questions.duplicate.answer' },
      ]
    },
    {
      id: 'ai',
      icon: '',
      lucideIcon: 'bot',
      titleKey: 'help.faq.categories.ai.title',
      questions: [
        { id: 'importBrochure', questionKey: 'help.faq.categories.ai.questions.importBrochure.question', answerKey: 'help.faq.categories.ai.questions.importBrochure.answer' },
        { id: 'supportedFormats', questionKey: 'help.faq.categories.ai.questions.supportedFormats.question', answerKey: 'help.faq.categories.ai.questions.supportedFormats.answer' },
        { id: 'bulkAnalysis', questionKey: 'help.faq.categories.ai.questions.bulkAnalysis.question', answerKey: 'help.faq.categories.ai.questions.bulkAnalysis.answer' },
        { id: 'verifyData', questionKey: 'help.faq.categories.ai.questions.verifyData.question', answerKey: 'help.faq.categories.ai.questions.verifyData.answer' },
        { id: 'aiCalculates', questionKey: 'help.faq.categories.ai.questions.aiCalculates.question', answerKey: 'help.faq.categories.ai.questions.aiCalculates.answer' },
      ]
    },
    {
      id: 'exports',
      icon: '',
      lucideIcon: 'file-edit',
      titleKey: 'help.faq.categories.exports.title',
      questions: [
        { id: 'exportExcel', questionKey: 'help.faq.categories.exports.questions.exportExcel.question', answerKey: 'help.faq.categories.exports.questions.exportExcel.answer' },
        { id: 'generatePdf', questionKey: 'help.faq.categories.exports.questions.generatePdf.question', answerKey: 'help.faq.categories.exports.questions.generatePdf.answer' },
        { id: 'ecoScore', questionKey: 'help.faq.categories.exports.questions.ecoScore.question', answerKey: 'help.faq.categories.exports.questions.ecoScore.answer' },
        { id: 'equivalences', questionKey: 'help.faq.categories.exports.questions.equivalences.question', answerKey: 'help.faq.categories.exports.questions.equivalences.answer' },
      ]
    },
  ];

  selectedCategory = computed(() =>
    this.faqCategories.find(c => c.id === this.selectedCategoryId()) ?? this.faqCategories[0]
  );

  ngOnInit(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${environment.onboardingVideoId}?rel=0&modestbranding=1`
    );
  }

  selectCategory(categoryId: string): void {
    this.selectedCategoryId.set(categoryId);
    this.expandedPanels = [];
  }

  get allPanelsExpanded(): boolean {
    const category = this.selectedCategory();
    return category.questions.length > 0 && this.expandedPanels.length === category.questions.length;
  }

  toggleAllPanels(): void {
    if (this.allPanelsExpanded) {
      this.expandedPanels = [];
    } else {
      const category = this.selectedCategory();
      this.expandedPanels = category.questions.map((_, i) => `${category.id}-${i}`);
    }
  }

  async submitContactForm(): Promise<void> {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.submitSuccess.set(false);
    this.submitError.set(false);

    try {
      // TODO: Implement API call to /api/contact endpoint
      // For now, simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));

      this.submitSuccess.set(true);
      this.contactForm.reset();
    } catch {
      this.submitError.set(true);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
